import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import RequiredNoteConfirmationDialog from '../../../components/RequiredNoteConfirmationDialog';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { VesselCertificate } from '../../../types/vessel';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const VesselRejectionDialog = ({
  vesselId,
  certificateId,
  onClose,
  onConfirm,
}: {
  vesselId: string | null;
  certificateId: string | null;
  onClose: () => void;
  onConfirm: (certificate: VesselCertificate) => void;
}) => {
  const { rejectVesselCertificate } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  const rejectVesselCertificateMutation = useMutation({
    mutationFn: (note: string) =>
      rejectVesselCertificate(vesselId || '', certificateId || '', note),
    onSuccess: certificate => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.certificate-rejection-success'),
      });
      onConfirm(certificate);
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t('content.certificate-rejection-error'),
        ),
      });
      rejectVesselCertificateMutation.reset();
    },
  });

  const handleClose = () => {
    if (rejectVesselCertificateMutation.isPending) {
      return;
    }
    onClose();
  };

  return (
    <RequiredNoteConfirmationDialog
      open={Boolean(vesselId)}
      title={t('content.reject-vessel-certificate')}
      noteLabel={t('content.rejection-note-reason')}
      onConfirmRequest={note => {
        if (vesselId) {
          rejectVesselCertificateMutation.mutate(note);
        }
      }}
      onCloseRequest={handleClose}
      loading={rejectVesselCertificateMutation.isPending}
      disabled={rejectVesselCertificateMutation.isPending}
    />
  );
};

export default VesselRejectionDialog;
